import { useTranslation } from 'react-i18next'
import { Lock, Users, Workflow, X } from 'lucide-react'

import { cn } from '@/lib/utils'

/**
 * The armed workflow, shown inline after the placeholder (decision D3,
 * docs/plans/assistant-platform-v1-execution-plan.md), mirroring
 * `ActiveStudyTypeChip`: the footer's `WorkflowMenu` is the door, this chip is
 * the answer.
 *
 * Copies `ActiveStudyTypeChip`'s key property verbatim: the label is an inert
 * `<span>`, and only the trailing dismiss button is clickable, so a stray
 * click on the label cannot silently drop the armed workflow.
 */
interface ActiveWorkflowChipProps {
  displayName: string
  /**
   * Omitted for the LOCKED variant: a chip read off a live run rather than an
   * armed choice, which the reader cannot dismiss while the run is active. In
   * the dismiss button's place sits a `Lock` glyph with no click target. Home
   * always passes this and is unaffected.
   */
  onClear?: () => void
  className?: string
  testId?: string
  /**
   * `line` fits inside one composer text line (no extra padding, 24px cap) so
   * arming a workflow does not grow the placeholder row. Thread chips keep
   * the default density.
   */
  density?: 'default' | 'line'
  /**
   * Which of the two chips this is (plan 2026-09-14-twin-test-bound-audience,
   * D2): `workflow` names the armed command, `audience` names the panel the
   * command is bound to and renders lucide `Users` instead. ONE component for
   * both so the pair cannot drift in tone, size or density.
   *
   * The inert-label invariant holds for BOTH variants: there is no `onClick`
   * on either, and the `audience` chip renders NO trailing control at all:
   * neither a dismiss nor a lock (D3 — the command chip's `×` clears the pair,
   * re-arming is how the audience is changed, and a locked run shows one lock
   * per composer, on the command chip).
   */
  icon?: 'workflow' | 'audience'
}

export function ActiveWorkflowChip(props: ActiveWorkflowChipProps) {
  const {
    displayName,
    onClear,
    className,
    testId,
    density = 'default',
    icon = 'workflow',
  } = props
  const { t } = useTranslation('app')

  const rootTestId = testId ?? 'home-lab-active-workflow'
  const isLine = density === 'line'
  const isAudience = icon === 'audience'
  const iconClassName = isLine ? 'h-3 w-3 shrink-0' : 'h-3.5 w-3.5 shrink-0'
  const dismissClassName = isLine
    ? 'ml-0.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full'
    : 'ml-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full'

  return (
    <span
      className={cn(
        'home-lab-mode-swap inline-flex items-center rounded-full font-medium ring-1 ring-inset',
        /* Height is FIXED per density rather than left to the trailing
           control: the audience variant renders none, and without a set
           height it came out shorter than the command chip beside it. The
           end padding follows the same rule — tight against a dismiss or
           lock, and mirroring the start padding when there is nothing
           trailing, so the label never sits flush against the edge. */
        isLine
          ? 'h-6 max-h-6 gap-1 py-0 pl-2 text-[12px] leading-none'
          : 'h-7 gap-1.5 py-0 pl-2.5 text-[13px]',
        isAudience
          ? isLine
            ? 'pr-2'
            : 'pr-2.5'
          : isLine
            ? 'pr-0.5'
            : 'pr-1',
        /* Neutral tone, same as `ActiveStudyTypeChip`'s non-category chips: a
           workflow has no goal category to tint against. */
        'bg-[var(--home-pill-track-bg)] text-[var(--home-composer-fg)] ring-transparent',
        className,
      )}
      data-testid={rootTestId}
    >
      {icon === 'audience' ? (
        <Users
          className={iconClassName}
          aria-label={t('homeLab.audienceChipAria')}
          role="img"
        />
      ) : (
        <Workflow className={iconClassName} aria-hidden="true" />
      )}

      <span className="max-w-[220px] truncate leading-none">{displayName}</span>

      {isAudience ? null : onClear ? (
        <button
          type="button"
          onClick={onClear}
          aria-label={t('homeLab.removeChip', { name: displayName })}
          className={cn(
            dismissClassName,
            'opacity-55 transition-opacity duration-200 hover:opacity-100',
          )}
          data-testid={`${rootTestId}-clear`}
        >
          <X className={iconClassName} strokeWidth={2.4} />
        </button>
      ) : (
        <span
          className={cn(dismissClassName, 'opacity-55')}
          data-testid={`${rootTestId}-lock`}
          title={t('homeLab.activeTestLocked')}
          aria-label={t('homeLab.activeTestLockedAria')}
        >
          <Lock className={iconClassName} strokeWidth={2.4} aria-hidden="true" />
        </span>
      )}
    </span>
  )
}
